<template>
  <SettingsCard
    v-if="user.notificationSettings"
    :title="$t('settings.notification.title')"
    :subtitle="$t('settings.notification.subtitle')"
    icon="mdi-bell-ring"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <div class="pa-4">
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div>
            <span class="font-weight-bold">
              {{ $t('settings.notification.body-texts.0') }}<br />
            </span>
            <span class="text-grey">
              {{ $t('settings.notification.body-texts.1') }}
            </span>
          </div>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <v-form
            ref="phoneForm"
            v-model="isFormValid"
            name="update-user-phone"
            @submit.prevent="submit()"
          >
            <div class="justify-center d-flex mb-sm-0">
              <PhoneField
                v-model="notificationPhone"
                :disabled="!hasMobileNotifications"
                :loading="isLoading.phone"
                update-on-input
              />
            </div>
          </v-form>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div>
            <span class="font-weight-bold">
              {{ $t('settings.notification.body-texts.2') }}<br />
            </span>
            <span class="text-grey">
              {{ $t('settings.notification.body-texts.2') }}
            </span>
          </div>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          class="d-flex justify-end"
          :class="{ 'justify-center px-0': $vuetify.display.smAndDown }"
        >
          <div class="w-full">
            <v-row>
              <v-col
                cols="12"
                sm="6"
              />
              <v-col
                cols="6"
                sm="2"
                class="justify-center d-flex"
              >
                <div class="d-flex flex-column align-center">
                  <div class="text-subtitle-2">
                    {{ $t('settings.notification.options.0') }}
                  </div>
                  <v-icon> mdi-email-outline </v-icon>
                </div>
              </v-col>
              <v-col
                v-if="isCountrySupported"
                cols="6"
                sm="2"
                class="justify-center d-flex"
              >
                <div class="d-flex flex-column align-center">
                  <div class="text-subtitle-2">
                    {{ $t('settings.notification.options.1') }}
                  </div>
                  <v-icon> mdi-phone-message </v-icon>
                </div>
              </v-col>
              <v-col
                cols="6"
                sm="2"
                class="justify-center d-flex"
              >
                <div class="d-flex flex-column align-center">
                  <div class="text-subtitle-2">
                    {{ $t('settings.notification.options.2') }}
                  </div>
                  <v-icon> mdi-whatsapp </v-icon>
                </div>
              </v-col>
            </v-row>

            <Feature :feature-slug="featureNames.LEAD">
              <div>
                <!-- New Contact -->
                <v-row class="mt-0">
                  <v-col
                    cols="12"
                    sm="6"
                    class="d-flex align-center"
                  >
                    {{ $t('settings.notification.option-new') }}
                    <v-tooltip
                      v-if="
                        !notificationSettings.newConversion.email &&
                        !notificationSettings.newConversion.sms
                      "
                      location="top"
                      color="warning"
                    >
                      <template v-slot:activator="{ props }">
                        <!-- TODO: check unsupported prop: dark -->
                        <v-icon
                          color="warning"
                          v-bind="props"
                        >
                          mdi-alert-circle
                        </v-icon>
                      </template>
                      <span>{{ $t('settings.notification.no-notifications') }}</span>
                    </v-tooltip>
                  </v-col>
                  <v-col
                    cols="6"
                    sm="2"
                    class="justify-center d-flex"
                  >
                    <v-switch
                      v-model="notificationSettings.newConversion.email"
                      :loading="isLoading.newConversion.email"
                      density="compact"
                      color="primary"
                      @update:model-value="saveSettings('newConversion', 'email')"
                      hide-details
                    />
                  </v-col>
                  <v-col
                    v-if="isCountrySupported"
                    cols="6"
                    sm="2"
                    class="justify-center d-flex"
                  >
                    <v-tooltip
                      location="top"
                      :disabled="hasMobileNotifications && hasValidPhone"
                    >
                      <span>{{ $t('settings.notification.phone-required') }}</span>
                      <template #activator="{ props: smsTooltipProps }">
                        <div v-bind="smsTooltipProps">
                          <v-switch
                            v-model="notificationSettings.newConversion.sms"
                            :disabled="!hasValidPhone || !hasMobileNotifications"
                            :loading="isLoading.newConversion.sms"
                            density="compact"
                            hide-details
                            color="primary"
                            @update:model-value="saveSettings('newConversion', 'sms')"
                          />
                        </div>
                      </template>
                    </v-tooltip>
                  </v-col>
                </v-row>

                <v-row class="mt-0">
                  <v-col
                    cols="12"
                    sm="6"
                    class="d-flex align-center"
                  >
                    {{ $t('settings.notification.option-due-date-reminder') }}
                    <v-tooltip
                      v-if="
                        !user.notificationSettings?.dueDate?.email &&
                        !user.notificationSettings?.dueDate?.sms
                      "
                      location="top"
                      color="warning"
                    >
                      <template v-slot:activator="{ props }">
                        <!-- TODO: check unsupported prop: dark -->
                        <v-icon
                          color="warning"
                          v-bind="props"
                        >
                          mdi-alert-circle
                        </v-icon>
                      </template>
                      <span>{{ $t('settings.notification.no-notifications') }}</span>
                    </v-tooltip>
                  </v-col>
                  <v-col
                    cols="6"
                    sm="2"
                    class="justify-center d-flex"
                  >
                    <v-switch
                      color="primary"
                      v-model="notificationSettings.dueDate.email"
                      :loading="isLoading.dueDate?.email"
                      @update:modelValue="saveSettings('dueDate', 'email')"
                    />
                  </v-col>
                  <v-col
                    v-if="isCountrySupported"
                    cols="6"
                    sm="2"
                    class="justify-center d-flex"
                  >
                    <v-tooltip
                      location="top"
                      :disabled="hasMobileNotifications && hasValidPhone"
                    >
                      <span>{{ $t('settings.notification.phone-required') }}</span>
                      <template #activator="{ props: smsTooltipProps }">
                        <div v-bind="smsTooltipProps">
                          <v-switch
                            color="primary"
                            v-model="notificationSettings.dueDate.sms"
                            :disabled="!hasValidPhone || !hasMobileNotifications"
                            :loading="isLoading.dueDate?.sms"
                            @update:model-value="saveSettings('dueDate', 'sms')"
                          />
                        </div>
                      </template>
                    </v-tooltip>
                  </v-col>
                </v-row>

                <!-- Contact assigned -->
                <Feature :feature-slug="featureNames.ASSIGN_LEADS">
                  <v-row class="mt-0">
                    <v-col
                      cols="12"
                      sm="6"
                      class="d-flex align-center"
                    >
                      {{ $t('settings.notification.option-assigned') }}
                      <v-tooltip
                        v-if="
                          !notificationSettings.leadAssigned.email &&
                          !notificationSettings.leadAssigned.sms
                        "
                        location="top"
                        color="warning"
                      >
                        <template v-slot:activator="{ props }">
                          <!-- TODO: check unsupported prop: dark -->
                          <v-icon
                            color="warning"
                            v-bind="props"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('settings.notification.no-notifications') }}</span>
                      </v-tooltip>
                    </v-col>
                    <v-col
                      cols="6"
                      sm="2"
                      class="justify-center d-flex"
                    >
                      <v-switch
                        v-model="notificationSettings.leadAssigned.email"
                        :loading="isLoading.leadAssigned.email"
                        density="compact"
                        color="primary"
                        @update:model-value="saveSettings('leadAssigned', 'email')"
                        hide-details
                      />
                    </v-col>
                    <v-col
                      v-if="isCountrySupported"
                      cols="6"
                      sm="2"
                      class="justify-center d-flex"
                    >
                      <v-tooltip
                        location="top"
                        :disabled="hasMobileNotifications && hasValidPhone"
                      >
                        <span>{{ $t('settings.notification.phone-required') }}</span>
                        <template #activator="{ props: smsTooltipProps }">
                          <div v-bind="smsTooltipProps">
                            <v-switch
                              v-model="notificationSettings.leadAssigned.sms"
                              :disabled="!hasValidPhone || !hasMobileNotifications"
                              :loading="isLoading.leadAssigned.sms"
                              color="primary"
                              density="compact"
                              @update:model-value="saveSettings('leadAssigned', 'sms')"
                              hide-details
                            />
                          </div>
                        </template>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </Feature>
              </div>
            </Feature>

            <Feature
              v-slot="{ feature }"
              :feature-slug="featureNames.CONTENT_CREATOR"
            >
              <v-row
                class="mt-0"
                v-if="
                  feature.config.schedulePost &&
                  feature.config.workdaysBeforeContentDepletionNotification.length > 0
                "
              >
                <v-col
                  sm="6"
                  cols="12"
                  class="align-center d-flex"
                >
                  <div>
                    {{ $t('settings.notification.content-depletion') }}

                    <div class="text-grey caption mt-n1">
                      {{
                        $t('settings.notification.content-depletion-subtitle', {
                          days: feature.config.workdaysBeforeContentDepletionNotification
                            .join(', ')
                            .replace(/,(?=[^,]*$)/, ' ' + $t('labels.and')),
                        })
                      }}
                    </div>
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  sm="2"
                  class="justify-center d-flex"
                >
                  <v-switch
                    color="primary"
                    v-model="notificationSettings.contentDepletion.email"
                    :loading="isLoading.contentDepletion.email"
                    @update:modelValue="saveSettings('contentDepletion', 'email')"
                  />
                </v-col>
              </v-row>
            </Feature>

            <Feature
              v-slot="{ feature }"
              :feature-slug="featureNames.CONTENT_CREATOR"
            >
              <v-row
                class="mt-0"
                v-if="feature.config.schedulePost"
              >
                <v-col
                  sm="6"
                  cols="12"
                  class="align-center d-flex"
                >
                  <div>
                    {{ $t('settings.notification.post-reminder') }}

                    <div class="text-grey caption mt-n1">
                      {{ $t('settings.notification.post-reminder-subtitle') }}
                    </div>
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  sm="2"
                  class="justify-center d-flex"
                >
                  <v-switch
                    color="primary"
                    v-model="notificationSettings.socialMediaPostReminder.email"
                    :loading="isLoading.socialMediaPostReminder.email"
                    @update:modelValue="saveSettings('socialMediaPostReminder', 'email')"
                  />
                </v-col>
              </v-row>
            </Feature>

            <!-- General -->
            <Feature
              v-slot="{ feature }"
              :feature-slug="featureNames.NOTIFICATIONS"
            >
              <template v-if="feature.config.hasGeneralNotifications">
                <v-row class="mt-0">
                  <v-col
                    sm="6"
                    cols="12"
                    class="align-center d-flex"
                  >
                    <div>
                      {{ $t('settings.notification.option-overall') }}

                      <div class="text-grey text-caption mt-n1">
                        {{
                          $t('settings.notification.option-overall-subtitle', {
                            readablePlatformName,
                          })
                        }}
                      </div>
                    </div>
                    <v-tooltip
                      v-if="!notificationSettings.news.email && !notificationSettings.news.whatsapp"
                      location="top"
                      color="warning"
                    >
                      <template v-slot:activator="{ props }">
                        <!-- TODO: check unsupported prop: dark -->
                        <v-icon
                          color="warning"
                          class="pl-2"
                          v-bind="props"
                        >
                          mdi-alert-circle
                        </v-icon>
                      </template>
                      <span>{{ $t('settings.notification.no-notifications') }}</span>
                    </v-tooltip>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="2"
                    class="justify-center d-flex"
                  >
                    <v-switch
                      v-model="notificationSettings.news.email"
                      :loading="isLoading.news.email"
                      color="primary"
                      density="compact"
                      @update:model-value="saveSettings('news', 'email')"
                      hide-details
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="2"
                  />
                  <v-col
                    cols="12"
                    sm="2"
                    class="justify-center d-flex"
                  >
                    <v-tooltip
                      location="top"
                      :disabled="hasMobileNotifications && hasValidPhone"
                    >
                      {{ $t('settings.notification.phone-required') }}
                      <template #activator="{ props: whatsappTooltipProps }">
                        <div v-bind="whatsappTooltipProps">
                          <v-switch
                            v-model="notificationSettings.news.whatsapp"
                            :loading="isLoading.news.whatsapp"
                            :disabled="
                              !hasValidPhone || !hasMobileNotifications || isLoading.news.whatsapp
                            "
                            color="primary"
                            density="compact"
                            class="mt-1"
                            @update:model-value="saveSettings('news', 'whatsapp')"
                            hide-details
                          />
                        </div>
                      </template>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </template>
            </Feature>
          </div>
        </v-col>
      </v-row>
      <Feature
        v-if="!notificationSettings.news.whatsapp"
        v-slot="{ feature }"
        :feature-slug="featureNames.NOTIFICATIONS"
      >
        <template v-if="feature.config.hasGeneralNotifications">
          <v-row>
            <v-col
              sm="6"
              cols="12"
              class="align-center d-flex"
            />
            <v-col
              cols="6"
              sm="4"
            >
              <v-alert
                class="whatsapp-tips-alert"
                color="#175E51"
                variant="tonal"
                :text="$t('settings.notification.whatsapp-tips-text')"
              >
                <template #title>
                  <div class="d-flex align-center ga-1">
                    <v-icon size="x-large">mdi-rocket-outline</v-icon>
                    {{ $t('settings.notification.whatsapp-tips-title') }}
                  </div>
                </template>
              </v-alert>
            </v-col>
          </v-row>
        </template>
      </Feature>
    </div>

    <v-alert
      v-if="!isPlatformTurkoglu"
      icon="mdi-lightbulb-on-20"
      prominent
      type="info"
      variant="tonal"
      class="ma-0 mt-4 mb-6"
    >
      <p class="ma-0 font-weight-bold">{{ $t('labels.hint') }}:</p>
      <ul
        v-if="hasMobileNotifications"
        style="list-style: disc"
      >
        <li>{{ $t('alerts.settings.notification.info') }}</li>
        <li>{{ $t('alerts.settings.notification.info-platform', { readablePlatformName }) }}</li>
      </ul>
      <div
        v-else
        class="d-flex flex-md-row flex-column justify-space-between align-center"
      >
        <span
          :class="{ 'w-full mt-4': $vuetify.display.smAndDown, 'ml-4': $vuetify.display.mdAndUp }"
        >
          {{ $t('alerts.settings.notification.info-product') }}
        </span>

        <Permission
          v-if="showUpgradeButton"
          permission="subscription:change"
        >
          <Feature :feature-slug="featureNames.CHANGE_SUBSCRIPTION">
            <v-btn
              color="info"
              :class="{
                'w-full mt-4': $vuetify.display.smAndDown,
                'mr-4': $vuetify.display.mdAndUp,
              }"
              @click="isUpgradeDialogOpen = true"
            >
              {{ $t('buttons.upgrade-now.cta') }}
            </v-btn>
          </Feature>
        </Permission>
      </div>
    </v-alert>
    <ChangeSubscription
      :dialog="isUpgradeDialogOpen"
      current-package="LIGHT"
      :company="company"
      :hide-downgrade="true"
      @close="isUpgradeDialogOpen = false"
    />
  </SettingsCard>
</template>
<script lang="ts">
import { computed, inject } from 'vue'
import { VForm } from 'vuetify/components'

import Permission from '@/components/Permission'
import PhoneField from '@/components/PhoneField.vue'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import { Company } from '@/generated/graphql'
import bus, { eventNames } from '@/lib/eventBus'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'

import ChangeSubscription from '../accountSettings/ChangeSubscription/index.vue'
import UPDATE_USER_NOTIFICATION_SETTINGS from './queries/UpdateUserNotificationSettings.gql'
import UPDATE_USER_PHONE from './queries/UpdateUserPhone.gql'

type NotificationSettings = {
  contentDepletion?: {
    email?: boolean
  }
  dueDate?: {
    email?: boolean
    sms?: boolean
  }
  leadAssigned?: {
    email?: boolean
    sms?: boolean
  }
  newConversion?: {
    email?: boolean
    sms?: boolean
  }
  news?: {
    email?: boolean
    whatsapp?: boolean
  }
  reminder?: {
    email?: boolean
  }
  socialMediaPostReminder?: {
    email?: boolean
  }
}

export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'notification' })
    const companyContext = inject<Company>('companyBase')

    const isCountrySupported = computed(() => {
      if (companyContext?.country && companyContext.country === 'us') return false
      return true
    })
    return {
      isOpen,
      setOpen,
      isCountrySupported,
    }
  },
  components: { SettingsCard, PhoneField, ChangeSubscription, Permission },
  mixins: [brandingMixin, featureMixin],
  props: {
    company: {
      type: Object,
      default: () => ({}),
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    refetchUser: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      // @ts-expect-error
      notificationSettings: this.createSettings(),
      notificationPhone: this.user.phone,
      isFormValid: false,
      spinnerTimeout: null,
      debounceTimeout: null,
      isUpgradeDialogOpen: false,
      isLoading: {
        leadAssigned: {
          sms: false,
          email: false,
        },
        newConversion: {
          sms: false,
          email: false,
        },
        news: {
          email: false,
          whatsapp: false,
        },
        reminder: {
          mail: false,
        },
        dueDate: {
          sms: false,
          email: false,
        },
        contentDepletion: {
          email: false,
        },
        socialMediaPostReminder: {
          email: false,
        },
        phone: false,
      },
    }
  },
  computed: {
    isPhoneDirty() {
      return this.notificationPhone && this.user.phone !== this.notificationPhone
    },
    hasValidPhone() {
      return this.user.phone && this.user.phone.length > 0
    },
    hasMobileNotifications() {
      return this.getFeature(this.featureNames.NOTIFICATIONS).config.hasMobileNotifications
    },
    showUpgradeButton() {
      return !this.isPlatformMarkero
    },
  },

  mounted() {
    this.notificationSettings = this.createSettings(this.user.notificationSettings)
  },

  watch: {
    notificationPhone() {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.isLoading.phone = true
        this.spinnerTimeout = setTimeout(() => {
          this.submit()
        }, 1000)
      }, 2000)
    },
    'user.notificationSettings': {
      handler(newSettings) {
        this.notificationSettings = this.createSettings(newSettings)
      },
      deep: true,
    },
  },
  methods: {
    createSettings(settings: NotificationSettings = {}) {
      return {
        contentDepletion: {
          email: settings.contentDepletion?.email || false,
        },
        dueDate: {
          email: settings.dueDate?.email || false,
          sms: settings.dueDate?.sms || false,
        },
        leadAssigned: {
          email: settings.leadAssigned?.email || false,
          sms: settings.leadAssigned?.sms || false,
        },
        newConversion: {
          email: settings.newConversion?.email || false,
          sms: settings.newConversion?.sms || false,
        },
        news: {
          email: settings.news?.email || false,
          whatsapp: settings.news?.whatsapp || false,
        },
        reminder: {
          email: settings.reminder?.email || false,
        },
        socialMediaPostReminder: {
          email: settings.socialMediaPostReminder?.email || false,
        },
      }
    },

    async saveSettings(notification, source) {
      this.isLoading[notification][source] = true
      const enabled = this.user.notificationSettings[notification][source] ?? false

      const trackingEventValue = enabled ? 'Disable' : 'Enable'
      this.$tracking.event('Settings', 'Clicked', `${notification}-${source}`, trackingEventValue)
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_USER_NOTIFICATION_SETTINGS,
          variables: {
            input: { notification, source, enabled: !enabled },
          },
        })

        this.spinnerTimeout = setTimeout(() => {
          this.refetchUser()
          this.isLoading[notification][source] = false
          bus.$emit(eventNames.SHOW_SNACKBAR, {
            color: 'success',
            text: this.$t('alerts.settings.notification.success'),
          })
        }, 1000)
      } catch (e) {
        this.spinnerTimeout = setTimeout(() => {
          this.refetchUser()
          this.isLoading[notification][source] = false
          bus.$emit(eventNames.SHOW_SNACKBAR, {
            color: 'error',
            text: this.$t('alerts.settings.notification.error'),
          })
        }, 1000)
      }
    },

    async submit() {
      const phoneForm = this.$refs.phoneForm as VForm
      const { valid } = await phoneForm.validate()
      if (valid) {
        try {
          await this.$apollo.mutate({
            mutation: UPDATE_USER_PHONE,
            variables: {
              input: { phone: this.notificationPhone },
            },
          })
          this.isLoading.phone = false
          this.refetchUser()
          bus.$emit(eventNames.SHOW_SNACKBAR, {
            color: 'success',
            text: this.$t('alerts.settings.notification.success-mobile'),
          })
        } catch (e) {
          if (e.graphQLErrors?.[0]?.message === 'INVALID_PHONE_ERROR') {
            bus.$emit(eventNames.SHOW_SNACKBAR, {
              color: 'error',
              text: this.$t('alerts.settings.notification.error-mobile'),
            })
          } else {
            bus.$emit(eventNames.SHOW_SNACKBAR, {
              color: 'error',
              text: this.$t('alerts.settings.notification.error'),
            })
          }
          this.refetchUser()
          this.isLoading.phone = false
        }
      }
    },
  },

  unmounted() {
    clearTimeout(this.spinnerTimeout)
    clearTimeout(this.debounceTimeout)
  },
}
</script>

<style scoped>
.v-input--selection-controls {
  margin-top: 0;
  max-height: 30px;
}
:deep(.v-input__details) {
  display: none;
}

:deep(.v-selection-control) {
  align-items: center;
}

.whatsapp-tips-alert :deep(.v-alert__content),
.whatsapp-tips-alert :deep(.v-alert-title) {
  font-size: 16px;
}
</style>
